import React, { FunctionComponent } from 'react';
import { match } from 'react-router-dom';
import { Grid, Header } from 'semantic-ui-react';
import withStyles from 'react-jss';
import { isEmpty, map, addIndex } from 'ramda';
import classnames from 'classnames';

import {
  useMerchantOverview,
  KeyMetricView,
} from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantOverview/MerchantOverview.context';
import { useUserMgmt } from 'components/UserMgmt/UserMgmt.context';
import { ClassProp } from 'helpers/types';
import { Theme } from 'config';
import { useDatePicker } from 'components/DatePicker/DatePicker.context';
import { ChartPlaceholder } from 'components/Placeholder';
import Query from 'components/Query';

import Transactions from './components/Transactions';
import Sales from './components/Sales';
import AOV from './components/AOV';
import Lift from './components/Lift';
import getQuery from './MerchantLift.queries';
import { getResults } from './MerchantLift.helpers';

type Styles = ClassProp<ReturnType<typeof styles>>;

export type Params = {
  merchantId: string;
  merchantName: string;
  startDate: string;
  endDate: string;
};

type Props = {
  match: match;
  classes: Styles;
};

const Components = { Transactions, Sales, AOV, Lift };
const LiftComponents = ['Sales', 'Transactions', 'AOV', 'Lift'];

const liftTitles = {
  Transactions: 'Transaction Summary',
  Sales: 'Sales Summary',
  AOV: 'AOV - Daily Moving Average',
  Lift: 'Lift - Daily Moving Average',
};

const styles = (theme: Theme) => ({
  container: {
    width: '100%',
    marginTop: '2.25rem !important',
    justifyContent: 'center !important',
    '&.external': {
      marginTop: '-2rem !important',
    },
  },
  grid: {
    position: 'relative',
  },
  chartRow: {
    margin: '1.25rem !important',
  },
  headerBlock: {
    background: '#fff !important',
    padding: '2rem !important',
  },
  header: {
    color: `${theme.darkGray} !important`,
  },
  loading: {
    '&&': {
      margin: '0 -1rem !important',
    },
  },
});

const mapIndexed = addIndex<any>(map);

const renderGraphs = (classes: Styles, params: Params, response: any) => (
  liftGraph: KeyMetricView,
  index: number,
) => {
  const GraphComponent = Components[liftGraph];
  const graphTitle = liftTitles[liftGraph];
  const data = getResults(response, liftGraph);

  return (
    <Grid.Row key={index} className={classes.chartRow}>
      <Grid.Column width={16} className={classes.headerBlock}>
        <Header className={classes.header} as="h3">
          {graphTitle}
        </Header>
      </Grid.Column>

      <GraphComponent data={data} params={params} />
    </Grid.Row>
  );
};

const renderKeyMetricGraphs = (classes: Styles, params: Params) => (
  response: any,
) => {
  if (response.loading) {
    return <ChartPlaceholder className={classes.loading} />;
  }

  return (
    <Grid className={classes.grid}>
      {mapIndexed(
        renderGraphs(classes, params, response),
        LiftComponents as any,
      )}
    </Grid>
  );
};

const MerchantLift: FunctionComponent<Props> = ({ classes, match }) => {
  const { userProfile } = useMerchantOverview();
  const { startDate, endDate } = useDatePicker();
  const { isExternalUser, isDemoUser } = useUserMgmt();
  const QUERY = getQuery(isExternalUser && !isDemoUser);
  const { merchantName, merchantId }: any =
    isEmpty(match.params) || isDemoUser ? userProfile : match.params;

  const params = {
    startDate,
    endDate,
    merchantId,
    merchantName,
  };

  return (
    <div
      className={classnames(classes.container, { external: isExternalUser })}
    >
      <Query
        enableProgressInfo
        query={QUERY}
        render={renderKeyMetricGraphs(classes, params)}
        variables={{ params }}
      />
    </div>
  );
};

export default withStyles(styles)(MerchantLift);
